import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import firebase from '../../Firebase';
import Cookies from 'js-cookie';
import './AgeVerificationTab.css';
import DobInput from '../DobInput';

const AgeVerificationTab = ({ setAgeVerifyInfo }) => {
  const { user } = useAuthContext();
  const [kycVerified, setKycVerified] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loading, setLoading] = useState(true);
  const [kycLoading, setKycLoading] = useState(false);
  const [savingDob, setSavingDob] = useState(false);
  const [error, setError] = useState('');
  const [showUnder18, setShowUnder18] = useState(false);
  const abortRef = useRef(new AbortController());

  const getTokens = async () => {
    const idToken = await firebase.auth().currentUser.getIdToken(true);
    const csrfToken = Cookies.get('mytoken');
    return { idToken, csrfToken };
  };

  useEffect(() => {
    if (!user?.userId) return;
    abortRef.current = new AbortController();

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { idToken, csrfToken } = await getTokens();
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ _id: user.userId }),
          signal: abortRef.current.signal,
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const data = await res.json();
        const verified = data.user?.kycVerified || false;
        setKycVerified(verified);
        setAgeVerifyInfo?.(verified);
        if (data.user?.dateOfBirth) {
          setDateOfBirth(new Date(data.user.dateOfBirth).toISOString().split('T')[0]);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('[Verifications]', e);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    return () => abortRef.current.abort();
  }, [user?.userId]);

  // Saves as soon as a complete date is entered (DobInput).
  const handleDobBlur = async (value = dateOfBirth) => {
    if (!value) return;
    setDateOfBirth(value);
    setShowUnder18(false);
    const age = new Date().getFullYear() - new Date(value).getFullYear();
    if (age < 18) {
      setShowUnder18(true);
      setDateOfBirth('');
      return;
    }
    setSavingDob(true);
    try {
      const { idToken, csrfToken } = await getTokens();
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          userId: user.userId,
          UserBasics: { dateOfBirth: new Date(value) },
        }),
      });
    } catch (e) {
      console.error('[Verifications] DOB save error:', e);
    } finally {
      setSavingDob(false);
    }
  };

  const startVerification = async () => {
    setError('');
    setKycLoading(true);
    try {
      const { idToken } = await getTokens();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/didit/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.url) {
        localStorage.setItem('kycReturnPath', '/ProfileUpdate');
        window.location.href = data.url;
      } else {
        setError(data.error || 'Could not start verification. Please try again.');
        setKycLoading(false);
      }
    } catch (e) {
      console.error('[Verifications] KYC error:', e);
      setError('Verification failed. Please try again.');
      setKycLoading(false);
    }
  };

  const formatDob = (isoStr) =>
    new Date(isoStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return <div className="avt-loading">Loading...</div>;

  return (
    <div className="avt-wrap">
      {kycVerified ? (
        <div className="avt-verified">
          <div className="avt-verified-tick">✓ Age verified</div>
          {dateOfBirth && (
            <div className="avt-dob-row">
              <span className="avt-label">Date of birth</span>
              <span className="avt-value">{formatDob(dateOfBirth)}</span>
              <span className="avt-locked">🔒 Locked after verification</span>
            </div>
          )}
        </div>
      ) : (
        <div className="avt-unverified">
          <p className="avt-intro">
            Confirm you are 18+ with a quick face scan — no ID document needed. Didit will do a liveness check and estimate your age. Once verified, your date of birth will be locked.
          </p>

          <div className="avt-section">
            <label className="avt-section-label" htmlFor="avt-dob">Date of birth</label>
            <DobInput id="avt-dob" value={dateOfBirth} onChange={handleDobBlur} />
            {savingDob && <span className="avt-saving">Saving…</span>}
            {showUnder18 && (
              <p className="avt-under18">You must be 18 or older to use this platform.</p>
            )}
          </div>

          {!dateOfBirth && (
            <div className="avt-prereqs">
              Please enter your date of birth above before verifying.
            </div>
          )}

          {error && <div className="avt-error">{error}</div>}

          <button
            className="avt-btn"
            onClick={startVerification}
            disabled={kycLoading || !dateOfBirth}
          >
            {kycLoading ? 'Preparing…' : 'Verify my age'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgeVerificationTab;
